/**
 * In-memory health backend for screens and tests (VITE_USE_MOCK). Shows every state the page must handle:
 * an all-ok group, warnings, two services down (fail twice in a row) + one first failure, unknowns, a service
 * that went down and recovered earlier today, a check never run yet, and the manual-run rate limit.
 * Times are relative to the mock clock (real time unless a test passes `clock`).
 * Role (same key as the session mock): localStorage `cf-chat.mock-role` = owner | head (default staff → 403).
 */
import { ApiError } from '../client'
import { countStatuses, sortChecks, statusRank, worstOf } from '../../lib/health'
import { HealthRateLimited } from './errors'
import {
  HEALTH_GROUPS,
  type HealthApi,
  type HealthCheck,
  type HealthData,
  type HealthGroup,
  type HealthIncident,
  type HealthPoint,
  type HealthSettings,
  type HealthStatus,
  type HealthSummary,
} from './types'

type Role = 'owner' | 'head' | 'staff'

export function mockHealthRole(): Role {
  try {
    const r = localStorage.getItem('cf-chat.mock-role')
    if (r === 'owner' || r === 'head') return r
  } catch {
    /* no storage */
  }
  return 'staff'
}

export const GROUP_NAMES: Record<string, string> = {
  chat: 'ระบบแชท',
  channels: 'ช่องทางแชท (LINE · Facebook · IG · เว็บ)',
  ai: 'AI และตรวจสลิป',
  web: 'เว็บและ POS',
  server: 'เซิร์ฟเวอร์ร้าน',
  sms: 'SMS แจ้งลูกค้า',
  delivery: 'ขนส่ง (Lalamove · DHL)',
  payment: 'รับชำระเงิน',
  alerts: 'แจ้งเตือนและการตลาด',
}

const MIN = 60_000
const HOUR = 60 * MIN

/**
 * One check of the mock. `changes`: [minutes ago, status] of the last 24 h+ (oldest first; the status before
 * the first change is `start`). `down` = minutes since the second failure in a row.
 */
interface Seed {
  key: string
  group: string
  name: string
  status: HealthStatus
  detail: string
  hint: string
  interval_s: number
  latency_ms?: number | null
  checked_min?: number // minutes ago (default ~interval/2)
  start?: HealthStatus
  changes?: [number, HealthStatus][]
  down_min?: number
  last_ok_min?: number | null
  never?: boolean
}

const SEEDS: Seed[] = [
  // ---- ระบบแชท (warn on connections)
  { key: 'chat.api', group: 'chat', name: 'API แชท (2 เครื่อง)', status: 'ok', detail: 'ตอบครบ 2 เครื่อง · เวอร์ชัน 6a99b32 ตรงกัน', hint: 'ถ้าล่ม: DO Console › Apps › cleanfit-chat › Runtime Logs · ย้อนเวอร์ชันที่ Activity › Rollback', interval_s: 60, latency_ms: 38 },
  { key: 'chat.db', group: 'chat', name: 'ฐานข้อมูลแชท (Postgres)', status: 'ok', detail: 'ตอบใน 4 ms', hint: 'ถ้าล่ม: DO Console › Databases › cleanfit-chat-db · ข้อความรอในคิวได้ราว 8 ชม. ไม่หาย', interval_s: 60, latency_ms: 4 },
  { key: 'chat.db_conn', group: 'chat', name: 'การเชื่อมต่อฐานข้อมูล', status: 'warn', detail: 'ใช้ 41 จาก 50 สาย (เกณฑ์เฝ้าระวัง 40)', hint: 'ดูว่ามีงานไหนเปิดสายค้าง (pg_stat_activity) · ถ้าเกิน 47 แชทจะเริ่มช้า', interval_s: 60, start: 'ok', changes: [[25, 'warn']] },
  { key: 'chat.db_size', group: 'chat', name: 'พื้นที่ฐานข้อมูล', status: 'ok', detail: '1.8 GB จาก 30 GB (6%)', hint: 'ถ้าเกิน 70%: ลบประวัติที่ไม่ใช้ หรือขยาย plan ของ DB', interval_s: 900 },
  { key: 'chat.ingest', group: 'chat', name: 'ตัวรับข้อความ (Cloudflare Worker)', status: 'ok', detail: 'ข้อความล่าสุดเข้าเมื่อ 3 นาทีที่แล้ว · คิวค้าง 0', hint: 'ถ้าล่ม: ดู log ของ Worker (runbook ข้อ 6) แล้ว replay จาก R2 (ข้อ 5)', interval_s: 120, latency_ms: 61 },
  { key: 'chat.watchdog', group: 'chat', name: 'ตัวเฝ้า webhook (ทุก 5 นาที)', status: 'ok', detail: 'รันล่าสุด 4 นาทีที่แล้ว · Meta, API, DLQ ปกติ', hint: 'ถ้าไม่รันเกิน 15 นาที: ดู Cron Triggers ของ Worker cleanfit-chat-ingest', interval_s: 300 },
  { key: 'chat.outbound', group: 'chat', name: 'คิวส่งข้อความตอบลูกค้า', status: 'ok', detail: 'ค้าง 0 · ส่งไม่สำเร็จใน 1 ชม. 0 รายการ', hint: 'ถ้าค้าง: ดูเหตุผลที่ฟองข้อความ "ส่งไม่สำเร็จ" และสวิตช์ CHAT_SEND_MODE', interval_s: 60 },
  { key: 'chat.r2_media', group: 'chat', name: 'คลังรูป/วิดีโอ (R2)', status: 'ok', detail: 'อ่านได้ปกติ', hint: 'ถ้าล่ม: Cloudflare Status › R2 · รูปในแชทจะโหลดไม่ขึ้นชั่วคราว', interval_s: 900, latency_ms: 142 },
  { key: 'chat.backup', group: 'chat', name: 'สำรองฐานข้อมูลแชททุกคืน (R2)', status: 'ok', detail: 'ชุดล่าสุด 02:30 วันนี้ · 18.4 MB · ครบทุกตาราง', hint: 'ถ้าเกิน 26 ชม.: DO Console › Apps › Jobs › db-backup (runbook ข้อ 17)', interval_s: 1800 },
  { key: 'chat.retention', group: 'chat', name: 'ลบข้อมูลแชทที่ไม่มีการติดต่อเกิน 5 ปี', status: 'ok', detail: 'รอบล่าสุด 03:40 · ลบ 0 ราย · รายถัดไปครบ 5 ปีเร็วสุด 31 ธ.ค. 2571', hint: 'ดู/เปลี่ยนที่ ตั้งค่า › ข้อมูลและยอดขาย · เกินเพดาน / นาฬิกาผิดปกติ: ระบบไม่ลบอะไร ทำตามคู่มือระบบ ข้อ 27', interval_s: 1800 },
  { key: 'chat.jobs', group: 'chat', name: 'งานเบื้องหลัง (แจกแชท · ปิดอัตโนมัติ)', status: 'ok', detail: 'แจกแชทล่าสุด 12 วินาทีที่แล้ว', hint: 'ถ้าหยุด: restart แอปใน DO Console', interval_s: 60 },
  // ---- ช่องทางแชท (unknown IG)
  { key: 'ch.line_test', group: 'channels', name: 'LINE OA ทดสอบ @447oxrpj', status: 'ok', detail: 'token ใช้ได้ · push เดือนนี้ 1,204 จาก 14,000', hint: 'ถ้า token ใช้ไม่ได้: LINE Developers › channel › Messaging API', interval_s: 900, latency_ms: 188 },
  { key: 'ch.meta_page', group: 'channels', name: 'เพจ Facebook ทดสอบ', status: 'ok', detail: 'token ใช้ได้ถึง 25 ธ.ค. · รับเหตุการณ์ครบ 6 ประเภท', hint: 'ต่ออายุ token ก่อนหมดตาม runbook ข้อ 20', interval_s: 900, latency_ms: 233 },
  { key: 'ch.ig', group: 'channels', name: 'Instagram', status: 'unknown', detail: 'ยังไม่ได้ผูกบัญชี IG กับเพจทดสอบ', hint: 'ผูก IG กับเพจใน Meta Business Suite แล้วระบบจะเริ่มตรวจเอง', interval_s: 3600 },
  { key: 'ch.webchat', group: 'channels', name: 'แชทบนเว็บ', status: 'ok', detail: 'เปิดใช้ (หน้าทดสอบ) · ยังไม่ได้ติดบนเว็บร้าน', hint: 'ถ้าล่ม: ตรวจ CHAT_WEBCHAT_SECRET ใน .do/app.yaml', interval_s: 300 },
  // ---- AI (all ok but a never-run check)
  { key: 'ai.gemini', group: 'ai', name: 'Gemini (AI ร่างคำตอบ)', status: 'ok', detail: 'ต่อได้ · ตรวจแบบไม่เสียเงิน', hint: 'ถ้าล่ม: ดู status.cloud.google.com · AI ร่างคำตอบจะหยุดชั่วคราว พนักงานตอบเองได้', interval_s: 900, latency_ms: 221 },
  { key: 'ai.jev', group: 'ai', name: 'Jev / TypeSafe', status: 'ok', detail: 'ต่อได้', hint: 'ถ้าล่ม: คำตอบด่วน Jev จะไม่ขึ้น ไม่กระทบการตอบแชท', interval_s: 900, latency_ms: 310 },
  { key: 'ai.worker', group: 'ai', name: 'AI ทำงานเบื้องหลัง', status: 'ok', detail: 'ล้มเหลว 0 จาก 42 ครั้งใน 1 ชม.', hint: 'ถ้าล้มบ่อย: ดู log ai_run_failed', interval_s: 300 },
  { key: 'ai.slip', group: 'ai', name: 'ตรวจสลิป', status: 'unknown', detail: 'ยังไม่เคยตรวจ', hint: 'ตั้งค่าบัญชีร้านที่ ตั้งค่า › ตรวจสลิป', interval_s: 3600, never: true },
  { key: 'ai.pos_beta', group: 'ai', name: 'POS beta (ลงออเดอร์จากแชท)', status: 'ok', detail: 'อ่านเมนูได้ · 212 รายการ', hint: 'ถ้าล่ม: ปุ่มลงออเดอร์ในแชทจะใช้ไม่ได้ ลงที่ POS โดยตรงแทน', interval_s: 900, latency_ms: 402 },
  // ---- เว็บ (down + first failure + TLS warn)
  { key: 'web.order', group: 'web', name: 'เว็บสั่งซื้อ /order', status: 'fail', detail: 'ไม่ตอบใน 10 วินาที (timeout) 3 ครั้งติด', hint: 'เปิดเว็บเองดูก่อน · ถ้าไม่ขึ้น: DirectAdmin › Apache/PHP-FPM และ Cloudflare (524 = PHP ช้าเกิน 100 วินาที)', interval_s: 120, latency_ms: null, start: 'ok', changes: [[8, 'fail']], down_min: 6, last_ok_min: 10 },
  { key: 'web.messenger', group: 'web', name: 'หน้าส่งของ lalamove.cleanfitdelivery.com', status: 'fail', detail: 'ตอบ 502 ครั้งแรก · รอยืนยันรอบถัดไป', hint: 'ถ้ายังพัง: ดู container ของ messenger บนเซิร์ฟเวอร์ (port 9000)', interval_s: 120, latency_ms: 1210, start: 'ok', changes: [[1, 'fail']], last_ok_min: 3 },
  { key: 'web.home', group: 'web', name: 'เว็บร้าน cleanfitdelivery.com', status: 'ok', detail: 'ตอบ 200 ใน 0.38 วินาที', hint: 'ถ้าล่ม: DirectAdmin + Cloudflare', interval_s: 120, latency_ms: 380 },
  { key: 'web.order_beta', group: 'web', name: 'เว็บสั่งซื้อทดสอบ /order/beta', status: 'ok', detail: 'ตอบ 200 ใน 0.52 วินาที', hint: 'ถ้าล่ม: ดู push ล่าสุดของ orderbeta', interval_s: 300, latency_ms: 520 },
  { key: 'web.pos', group: 'web', name: 'POS cleanfit.cloud', status: 'ok', detail: 'หน้าเข้าระบบตอบ 200 ใน 0.29 วินาที', hint: 'ถ้าล่ม: DirectAdmin › cleanfit.cloud', interval_s: 120, latency_ms: 290 },
  { key: 'web.tls', group: 'web', name: 'ใบรับรอง HTTPS (5 โดเมน)', status: 'warn', detail: 'cleanfit.cloud หมดอายุใน 12 วัน (8 ต.ค.) · ที่เหลือเกิน 60 วัน', hint: 'DirectAdmin › SSL Certificates › ต่ออายุ Let’s Encrypt', interval_s: 21600, start: 'ok', changes: [[300, 'warn']] },
  // ---- เซิร์ฟเวอร์ร้าน (all ok → collapsed)
  { key: 'srv.probe', group: 'server', name: 'ตัวตรวจบนเซิร์ฟเวอร์ร้าน', status: 'ok', detail: 'ตอบใน 0.85 วินาที · เวลาเครื่องตรง', hint: 'ถ้าล่ม: ตรวจไฟล์ chat_health_probe.php และ secure_keys บน cleanfit-prod', interval_s: 300, latency_ms: 850 },
  { key: 'srv.mysql_root', group: 'server', name: 'ฐานข้อมูล POS (MySQL)', status: 'ok', detail: 'ต่อได้ใน 3 ms', hint: 'ถ้าล่ม: DirectAdmin › MySQL · POS และเว็บจะใช้ไม่ได้ทั้งหมด', interval_s: 300, latency_ms: 3 },
  { key: 'srv.disk', group: 'server', name: 'พื้นที่ดิสก์เซิร์ฟเวอร์', status: 'ok', detail: 'ว่าง 41% (80 GB)', hint: 'ถ้าต่ำกว่า 15%: ล้าง log เก่าและ backup ในเครื่อง', interval_s: 300 },
  { key: 'srv.cron_dhl', group: 'server', name: 'ดึงสถานะ DHL (cron ทุก 5 นาที)', status: 'ok', detail: 'รันล่าสุด 21:06 · สำเร็จ', hint: 'ดู crontab ของ admin และ log ของ poller', interval_s: 300 },
  { key: 'srv.restic', group: 'server', name: 'สำรองฐานข้อมูล POS ไป R2 (restic)', status: 'ok', detail: 'ชุดล่าสุด 03:00 วันนี้', hint: 'ถ้าเกิน 26 ชม.: ดู log ของ restic บน cleanfit-prod', interval_s: 1800 },
  // ---- SMS (down + warn)
  { key: 'srv.cron_sms', group: 'sms', name: 'ส่ง SMS แจ้งลูกค้า (cron ทุก 1 นาที)', status: 'fail', detail: 'ไม่ได้รันมา 47 นาที (ปกติทุก 1 นาที)', hint: 'ดู crontab และ log ของ sms_dispatch บน cleanfit-prod · ลูกค้าจะไม่ได้ SMS ยืนยัน', interval_s: 300, start: 'ok', changes: [[40, 'fail']], down_min: 35, last_ok_min: 47 },
  { key: 'srv.smsok_credit', group: 'sms', name: 'เครดิต SMSOK', status: 'warn', detail: 'เหลือ 820 ข้อความ (เกณฑ์เฝ้าระวัง 1,000)', hint: 'เติมเครดิตที่ SMSOK ก่อนหมด · ถ้าหมด ระบบจะใช้ ThaiBulkSMS สำรอง', interval_s: 1800, start: 'ok', changes: [[310, 'warn']] },
  { key: 'srv.sms_dlr', group: 'sms', name: 'รายงานส่งถึง (DLR) ล่าสุด', status: 'ok', detail: 'ล่าสุด 20:58 · ส่งไม่ถึงใน 1 ชม. 0%', hint: 'ถ้าไม่มี DLR นาน: ตรวจ webhook DLR ของ SMSOK', interval_s: 300 },
  // ---- ขนส่ง
  { key: 'srv.lalamove', group: 'delivery', name: 'Lalamove (webhook + API)', status: 'ok', detail: 'webhook ล่าสุด 21:02 · API ตอบ', hint: 'ถ้า webhook เงียบนาน: ดู lalamove webhook limits ใน runbook ของ route', interval_s: 300, latency_ms: 640 },
  { key: 'srv.dhl', group: 'delivery', name: 'DHL (poller + token)', status: 'ok', detail: 'ดึงสถานะสำเร็จล่าสุด 21:06 · token ใช้ได้', hint: 'ถ้าล้ม: ดู log ของ dhl poller', interval_s: 300 },
  // ---- ชำระเงิน
  { key: 'srv.beam', group: 'payment', name: 'Beam รับชำระบัตร', status: 'ok', detail: 'API ตอบ · webhook ล่าสุด 19:40', hint: 'ถ้า API ไม่ตอบ: ลูกค้าจ่ายบัตรไม่ได้ ให้ชำระด้วยการโอนแทน', interval_s: 900, latency_ms: 470 },
  // ---- แจ้งเตือนและการตลาด (recovered earlier)
  { key: 'srv.tg_order_bot', group: 'alerts', name: 'บอทแจ้งออเดอร์ Telegram', status: 'ok', detail: 'บอทตอบ (getMe)', hint: 'ถ้าล่ม: ตรวจ token ของ @CleanfitOrderBot', interval_s: 900, latency_ms: 120 },
  { key: 'srv.meta_capi', group: 'alerts', name: 'Meta CAPI (ส่ง conversion)', status: 'ok', detail: 'ส่งล่าสุด 20:51 · สำเร็จ', hint: 'ถ้าล้ม: ดู log capi บนเว็บ root', interval_s: 900, start: 'ok', changes: [[190, 'fail'], [100, 'ok']], last_ok_min: 5 },
  { key: 'srv.line_owner_bot', group: 'alerts', name: 'LINE bot สรุปยอดเจ้าของ (22:05)', status: 'ok', detail: 'ส่งล่าสุด เมื่อวาน 22:05', hint: 'ถ้าไม่ส่ง: ดู cron 22:05 บน cleanfit-prod', interval_s: 3600 },
  { key: 'srv.seo_cron', group: 'alerts', name: 'อันดับ SEO / AI Visibility (03:35)', status: 'ok', detail: 'รันล่าสุด 03:35 วันนี้', hint: 'ถ้าไม่รัน: ดู cron 03:35 บน cleanfit-prod', interval_s: 3600 },
]

/** Incidents of the mock (minutes ago, newest first once built). */
const INCIDENTS: { key: string; kind: HealthIncident['kind']; from: HealthStatus | null; to: HealthStatus; min: number; detail: string }[] = [
  { key: 'web.messenger', kind: 'change', from: 'ok', to: 'fail', min: 1, detail: 'ตอบ 502' },
  { key: 'web.order', kind: 'down', from: 'fail', to: 'fail', min: 6, detail: 'ไม่ตอบใน 10 วินาที 2 ครั้งติด' },
  { key: 'web.order', kind: 'change', from: 'ok', to: 'fail', min: 8, detail: 'ไม่ตอบใน 10 วินาที (timeout)' },
  { key: 'chat.db_conn', kind: 'change', from: 'ok', to: 'warn', min: 25, detail: 'ใช้ 41 จาก 50 สาย' },
  { key: 'srv.cron_sms', kind: 'down', from: 'fail', to: 'fail', min: 35, detail: 'ไม่ได้รันมา 12 นาที' },
  { key: 'srv.cron_sms', kind: 'change', from: 'ok', to: 'fail', min: 40, detail: 'ไม่ได้รันมา 7 นาที' },
  { key: 'srv.meta_capi', kind: 'recovered', from: 'fail', to: 'ok', min: 100, detail: 'ล่ม 1 ชม. 30 นาที' },
  { key: 'srv.meta_capi', kind: 'down', from: 'fail', to: 'fail', min: 175, detail: 'ส่งไม่สำเร็จ 2 ครั้งติด (HTTP 400)' },
  { key: 'web.tls', kind: 'change', from: 'ok', to: 'warn', min: 300, detail: 'cleanfit.cloud หมดอายุใน 12 วัน' },
  { key: 'srv.smsok_credit', kind: 'change', from: 'ok', to: 'warn', min: 310, detail: 'เหลือ 998 ข้อความ' },
]

const iso = (ms: number): string => new Date(ms).toISOString()

export class MockHealthApi implements HealthApi {
  settings: HealthSettings = { telegram_on: true, telegram_ready: true, can_edit: true }
  runs = 0
  private checkedShift = 0 // "ตรวจตอนนี้" makes every check fresh
  private lastManual: number | null = null
  private readonly latencyMs: number
  private readonly runMs: number
  private readonly clock: () => number
  private readonly role: () => Role

  constructor(opts: { latencyMs?: number; runMs?: number; clock?: () => number; role?: () => Role } = {}) {
    this.latencyMs = opts.latencyMs ?? 150
    this.runMs = opts.runMs ?? 1000
    this.clock = opts.clock ?? (() => Date.now())
    this.role = opts.role ?? mockHealthRole
  }

  private async wait(ms: number): Promise<void> {
    if (ms > 0) await new Promise((r) => setTimeout(r, ms))
  }

  private guard(): Role {
    const r = this.role()
    if (r === 'staff') throw new ApiError(403, 'เฉพาะผู้จัดการและเจ้าของเท่านั้น')
    return r
  }

  private check(s: Seed, now: number): HealthCheck {
    const checkedAgo = this.checkedShift ? 0.2 : (s.checked_min ?? Math.min(s.interval_s / 120, 9))
    const checked = s.never ? null : iso(now - checkedAgo * MIN)
    const timeline: HealthPoint[] = []
    if (!s.never) {
      timeline.push({ at: iso(now - 30 * HOUR), status: s.start ?? s.status })
      for (const [ago, st] of s.changes ?? []) timeline.push({ at: iso(now - ago * MIN), status: st })
    }
    const lastOk =
      s.never ? null : s.last_ok_min !== undefined ? (s.last_ok_min === null ? null : iso(now - s.last_ok_min * MIN)) : s.status === 'ok' ? checked : iso(now - 40 * HOUR)
    return {
      key: s.key,
      group: s.group,
      name: s.name,
      status: s.status,
      down: s.down_min !== undefined,
      down_since: s.down_min !== undefined ? iso(now - s.down_min * MIN) : null,
      latency_ms: s.latency_ms ?? null,
      checked_at: checked,
      last_ok_at: lastOk,
      detail: s.detail,
      hint: s.hint,
      interval_s: s.interval_s,
      timeline,
    }
  }

  private build(): HealthData {
    const now = this.clock()
    const checks = SEEDS.map((s) => this.check(s, now))
    const groups: HealthGroup[] = HEALTH_GROUPS.map((key) => {
      const mine = sortChecks(checks.filter((c) => c.group === key))
      return { key, name: GROUP_NAMES[key] ?? key, worst: worstOf(mine.map((c) => c.status)), counts: countStatuses(mine), checks: mine }
    })
      .filter((g) => g.checks.length)
      .sort((a, b) => statusRank(b.worst) - statusRank(a.worst) || HEALTH_GROUPS.indexOf(a.key as never) - HEALTH_GROUPS.indexOf(b.key as never))
    const counts = countStatuses(checks)
    const byKey = new Map(checks.map((c) => [c.key, c]))
    const incidents: HealthIncident[] = INCIDENTS.map((i, n) => ({
      id: 100 - n,
      key: i.key,
      name: byKey.get(i.key)?.name ?? i.key,
      group: byKey.get(i.key)?.group ?? 'chat',
      kind: i.kind,
      from: i.from,
      to: i.to,
      at: iso(now - i.min * MIN),
      detail: i.detail,
    }))
    const stamps = checks.map((c) => c.checked_at).filter((x): x is string => x !== null)
    return {
      now: iso(now),
      checked_at: stamps.sort().at(-1) ?? null,
      run: {
        last_run_at: iso(now - (this.checkedShift ? 0.2 : 0.6) * MIN),
        manual_next_at: this.lastManual !== null && now - this.lastManual < 60_000 ? iso(this.lastManual + 60_000) : null,
        running: false,
      },
      summary: { ...counts, down: checks.filter((c) => c.down).length, total: checks.length },
      settings: { ...this.settings, can_edit: this.role() === 'owner' },
      groups,
      incidents,
    }
  }

  async get(): Promise<HealthData> {
    await this.wait(this.latencyMs)
    this.guard()
    return this.build()
  }

  async summary(): Promise<HealthSummary> {
    await this.wait(this.latencyMs)
    this.guard()
    const d = this.build()
    const all = d.groups.flatMap((g) => g.checks)
    return {
      down: d.summary.down,
      down_items: all.filter((c) => c.down).map((c) => ({ key: c.key, name: c.name, since: c.down_since })),
      fail: d.summary.fail,
      warn: d.summary.warn,
      checked_at: d.checked_at,
    }
  }

  async run(): Promise<HealthData> {
    this.guard()
    const now = this.clock()
    if (this.lastManual !== null && now - this.lastManual < 60_000) {
      const left = Math.ceil((60_000 - (now - this.lastManual)) / 1000)
      await this.wait(this.latencyMs)
      throw new HealthRateLimited(`เพิ่งตรวจไป รอได้อีกครั้งในอีก ${left} วินาที`, left)
    }
    this.lastManual = now
    await this.wait(this.runMs)
    this.runs += 1
    this.checkedShift = 1
    return this.build()
  }

  async saveSettings(body: { telegram_on: boolean }): Promise<HealthSettings> {
    await this.wait(this.latencyMs)
    if (this.guard() !== 'owner') throw new ApiError(403, 'เฉพาะเจ้าของเท่านั้น')
    this.settings = { ...this.settings, telegram_on: !!body.telegram_on }
    return { ...this.settings, can_edit: true }
  }
}
